import { ProdutoService } from 'app/shared/services/produto.service';
import { ChangeDetectionStrategy, HostListener, Component, Inject, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Produto } from 'app/shared/interfaces/produto';
import { AppConfirmService } from 'app/shared/services/app-confirm/app-confirm.service';
import { LayoutService } from 'app/shared/services/layout.service';
import { BehaviorSubject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-produto-codbar-popup',
  templateUrl: './produto-codbar-popup.component.html',
  styleUrls: ['./produto-codbar-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProdutoCodbarPopupComponent implements OnInit, OnDestroy, AfterViewInit {
  public produtoForm: FormGroup;
  produto: Partial<Produto>;
  layoutConf: any;
  @ViewChild('mobileBarcodeVideo') mobileBarcodeVideoRef: ElementRef<HTMLVideoElement>;
  @ViewChild('scanBarcodeBtn1') scanBarcodeBtn1Ref: ElementRef<HTMLButtonElement>;
  mobileCameraScanning = false;
  mobileCameraBusy = false;
  activeBarcodeControl: 'codigobarra1' | 'codigobarra2' | 'codigobarra3' | 'codigobarra4' = 'codigobarra1';
  private mobileScannerStream: MediaStream | null = null;
  private mobileScannerIntervalId: number | null = null;

  maskCodigoBarra = {
    mask: '0000000000000{*}',
    lazy: true,
    placeholderChar: '_'
  };

  @HostListener('window:keyup.esc') onKeyUp() {
    this.confirmService.confirm({ message: `Deseja realmente fechar? Poderá haver perda de dados.` })
      .subscribe(res => {
        if (res) {
          this.stopMobileBarcodeCamera();
          this.dialogRef.close();
        }
      });
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ProdutoCodbarPopupComponent>,
    private fb: FormBuilder,
    private confirmService: AppConfirmService,
    private layout: LayoutService,
    private produtoService: ProdutoService,
    private snack: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.layoutConf = this.layout.layoutConf;
    this.produto = (this.data?.payload || {}) as Partial<Produto>;
    this.buildProdutoForm(this.produto);
    console.log(`Produto: ${this.produto?.codigo ?? ''} - ${this.produto?.descricao ?? ''}`);
  }

  ngAfterViewInit(): void {
    if (!this.isMobileLayout() || !this.isCameraScannerSupported()) {
      return;
    }

    setTimeout(() => {
      this.scanBarcodeBtn1Ref?.nativeElement?.focus();
    }, 50);
  }

  buildProdutoForm(produto: Partial<Produto>) {
    this.produtoForm = this.fb.group({
      id: [produto?.id],
      descricao: [produto?.descricao || ''],
      codigobarra1: [produto?.codigobarra1 || ''],
      codigobarra2: [produto?.codigobarra2 || ''],
      codigobarra3: [produto?.codigobarra3 || ''],
      codigobarra4: [produto?.codigobarra4 || '']
    });
  }

  ngOnDestroy(): void {
    this.stopMobileBarcodeCamera();
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        console.log(`${field} - ${control.valid}`);
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  save() {
    if (this.produtoForm.invalid) {
      this.validateAllFormFields(this.produtoForm);
      this.snack.open('Por favor, corrija os erros nos campos!', 'OK', { duration: 5500 });
      return;
    }

    const result = new BehaviorSubject<Produto>(null);
    this.produtoService.saveOrUpdate(this.produtoForm.value).subscribe(
      val => {
        this.snack.open('SALVO COM SUCESSO!', 'OK', { duration: 5500 });
        this.confirmService.confirm({
          title: 'SALVAR',
          message: 'OK, Produto Salvo com Sucesso!',
          error: false
        });
        result.next(val);
        this.dialogRef.close(val); // Fecha o dialog com o resultado
      },
      err => {
        this.snack.open(`ERRO: ${err.error?.status || 'Erro desconhecido'}!`, 'ERRO', { duration: 5500 });
        this.confirmService.confirm({
          title: 'ERRO AO SALVAR',
          message: err.error?.status || 'Erro desconhecido',
          error: true
        });
        result.error(err);
      }
    );
    return result;
  }

  close() {
    this.stopMobileBarcodeCamera();
    this.dialogRef.close();
  }

  isMobileLayout(): boolean {
    return !!this.layoutConf?.isMobile;
  }

  isCameraScannerSupported(): boolean {
    const barcodeDetectorCtor = (window as any)?.BarcodeDetector;
    return !!(barcodeDetectorCtor && navigator?.mediaDevices?.getUserMedia);
  }

  getControlLabel(controlName: string): string {
    return controlName.replace('codigobarra', 'Nº ');
  }

  private shouldUseCameraScanner(): boolean {
    if (this.layoutConf?.isMobile) {
      return true;
    }

    const ua = (navigator?.userAgent || '').toLowerCase();
    const byUa = /android|iphone|ipad|ipod|iemobile|opera mini/.test(ua);
    const byTouch = (navigator?.maxTouchPoints || 0) > 1 && window.innerWidth <= 1024;
    return byUa || byTouch;
  }

  async openMobileBarcodeCamera(controlName: 'codigobarra1' | 'codigobarra2' | 'codigobarra3' | 'codigobarra4') {
    this.activeBarcodeControl = controlName;

    if (!this.shouldUseCameraScanner()) {
      this.confirmService.confirm({
        message: `Leitura por câmera disponível apenas no mobile/tablet com navegador compatível.`,
        error: true,
      });
      return;
    }

    if (this.mobileCameraScanning) {
      this.stopMobileBarcodeCamera();
      return;
    }

    const barcodeDetectorCtor = (window as any)?.BarcodeDetector;
    if (!barcodeDetectorCtor) {
      this.confirmService.confirm({
        message: `Leitura por câmera não suportada neste navegador. Use os botões Colar e Validar abaixo do campo.`,
        error: true,
      });
      return;
    }

    if (!navigator?.mediaDevices?.getUserMedia) {
      this.confirmService.confirm({
        message: `Câmera indisponível neste ambiente. Use os botões Colar e Validar abaixo do campo.`,
        error: true,
      });
      return;
    }

    try {
      this.mobileScannerStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
        },
        audio: false,
      });

      this.mobileCameraScanning = true;
      this.cdr.detectChanges();

      const video = this.mobileBarcodeVideoRef?.nativeElement;
      if (!video) {
        this.stopMobileBarcodeCamera();
        return;
      }

      video.srcObject = this.mobileScannerStream;
      await video.play();
      this.startMobileBarcodeDetectLoop();
    } catch (_) {
      this.stopMobileBarcodeCamera();
      this.confirmService.confirm({
        message: `Não foi possível abrir a câmera. Verifique permissão no navegador.`,
        error: true,
      });
    }
  }

  onScanIconClick(
    controlName: 'codigobarra1' | 'codigobarra2' | 'codigobarra3' | 'codigobarra4',
    event: Event
  ) {
    event.preventDefault();
    event.stopPropagation();

    const active = document?.activeElement as HTMLElement | null;
    active?.blur();

    setTimeout(() => {
      this.openMobileBarcodeCamera(controlName);
    }, 0);
  }

  stopMobileBarcodeCamera() {
    if (this.mobileScannerIntervalId != null) {
      window.clearInterval(this.mobileScannerIntervalId);
      this.mobileScannerIntervalId = null;
    }

    const video = this.mobileBarcodeVideoRef?.nativeElement;
    if (video) {
      try {
        video.pause();
      } catch (_) {
      }
      video.srcObject = null;
    }

    if (this.mobileScannerStream) {
      this.mobileScannerStream.getTracks().forEach((track) => track.stop());
      this.mobileScannerStream = null;
    }

    this.mobileCameraBusy = false;
    this.mobileCameraScanning = false;
    this.cdr.markForCheck();
  }

  private startMobileBarcodeDetectLoop() {
    const barcodeDetectorCtor = (window as any)?.BarcodeDetector;
    if (!barcodeDetectorCtor) {
      return;
    }

    const detector = new barcodeDetectorCtor({
      formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'itf', 'codabar', 'qr_code'],
    });

    this.mobileScannerIntervalId = window.setInterval(async () => {
      if (!this.mobileCameraScanning || this.mobileCameraBusy) {
        return;
      }

      const video = this.mobileBarcodeVideoRef?.nativeElement;
      if (!video || video.readyState < 2) {
        return;
      }

      this.mobileCameraBusy = true;
      try {
        const found = await detector.detect(video);
        const rawValue = found?.[0]?.rawValue ? String(found[0].rawValue).trim() : '';
        if (rawValue) {
          this.produtoForm.get(this.activeBarcodeControl)?.setValue(rawValue);
          this.stopMobileBarcodeCamera();
          this.snack.open(`Código lido e preenchido no campo ${this.getControlLabel(this.activeBarcodeControl)}`, 'OK', { duration: 2500 });
        }
      } catch (_) {
      } finally {
        this.mobileCameraBusy = false;
      }
    }, 300);
  }

  async pasteBarcodeFromClipboard(controlName: 'codigobarra1' | 'codigobarra2' | 'codigobarra3' | 'codigobarra4') {
    if (!navigator?.clipboard?.readText) {
      this.confirmService.confirm({
        message: `Não foi possível acessar a área de transferência neste navegador.`,
        error: true,
      });
      return;
    }

    try {
      const text = await navigator.clipboard.readText();
      const raw = String(text || '').trim();
      const onlyDigits = raw.replace(/\D/g, '');
      const value = onlyDigits || raw;

      if (!value) {
        this.snack.open('Área de transferência vazia.', 'OK', { duration: 2500 });
        return;
      }

      this.produtoForm.get(controlName)?.setValue(value);
      this.snack.open(`Código colado no campo ${this.getControlLabel(controlName)}.`, 'OK', { duration: 2500 });
      this.cdr.markForCheck();
    } catch (_) {
      this.confirmService.confirm({
        message: `Falha ao ler área de transferência. Verifique permissão no navegador.`,
        error: true,
      });
    }
  }

  validateBarcodeValue(controlName: 'codigobarra1' | 'codigobarra2' | 'codigobarra3' | 'codigobarra4') {
    const current = String(this.produtoForm.get(controlName)?.value || '').trim();
    const digits = current.replace(/\D/g, '');

    if (!digits) {
      this.snack.open(`Informe um código no campo ${this.getControlLabel(controlName)}.`, 'OK', { duration: 2500 });
      return;
    }

    const valid = this.isValidBarcodeByChecksum(digits);
    if (valid) {
      this.snack.open(`Código ${this.getControlLabel(controlName)} válido.`, 'OK', { duration: 2500 });
      return;
    }

    this.confirmService.confirm({
      title: 'ATENÇÃO',
      message: `Código ${this.getControlLabel(controlName)} com formato/checksum inválido.`,
      error: true,
    });
  }

  private isValidBarcodeByChecksum(digits: string): boolean {
    if (!/^\d+$/.test(digits)) {
      return false;
    }

    if (digits.length === 8) {
      return this.validateEan8(digits);
    }

    if (digits.length === 12) {
      return this.validateUpcA(digits);
    }

    if (digits.length === 13) {
      return this.validateEan13(digits);
    }

    return false;
  }

  private validateEan13(value: string): boolean {
    const nums = value.split('').map((n) => Number(n));
    const check = nums[12];
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += nums[i] * (i % 2 === 0 ? 1 : 3);
    }
    const calc = (10 - (sum % 10)) % 10;
    return calc === check;
  }

  private validateEan8(value: string): boolean {
    const nums = value.split('').map((n) => Number(n));
    const check = nums[7];
    let sum = 0;
    for (let i = 0; i < 7; i++) {
      sum += nums[i] * (i % 2 === 0 ? 3 : 1);
    }
    const calc = (10 - (sum % 10)) % 10;
    return calc === check;
  }

  private validateUpcA(value: string): boolean {
    const normalized = value.length === 12 ? `0${value}` : value;
    return normalized.length === 13 && this.validateEan13(normalized);
  }
}